import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import DeleteIcon from '@mui/icons-material/Delete';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { logger } from '../../utils/logger';
import { getAllCities, getCityBySlug } from '../../config/cities';

const DealSuggestionsTable = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');

  useEffect(() => {
    const q = query(collection(db, 'deal_suggestions'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const suggestionsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Validate required fields
          if (!data.place_data) {
            logger.warn(`Missing place_data for suggestion ${doc.id}`);
            return null;
          }

          return {
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate() || new Date(),
            // Ensure place_data fields exist with fallbacks
            place_data: {
              name: data.place_data.name || 'Unknown Business',
              address: data.place_data.address || 'No address provided',
              ...data.place_data
            }
          };
        }).filter(Boolean); // Remove null entries

        setSuggestions(suggestionsData);
        setError(null);
      } catch (err) {
        logger.error('Error processing suggestions:', err);
        setError('Failed to load suggestions');
      } finally {
        setLoading(false);
      }
    }, (err) => {
      logger.error('Error fetching deal suggestions:', err);
      setError('Failed to fetch suggestions');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (suggestionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'deal_suggestions', suggestionId), {
        status: newStatus
      });
    } catch (error) {
      logger.error('Error updating suggestion status:', error);
    }
  };

  const handleDelete = async (suggestionId, suggestionName) => {
    if (!window.confirm(`Delete suggestion for "${suggestionName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'deal_suggestions', suggestionId));
      logger.info('Deleted suggestion:', suggestionId);
    } catch (error) {
      logger.error('Error deleting suggestion:', error);
    }
  };

  const handleCreateVenue = (suggestion) => {
    // Pass all available place_data to pre-fill the venue form
    const payload = {
      suggestionId: suggestion.id,
      fromSuggestion: true,
      // Place data from Google Maps (already stored in suggestion)
      headline: suggestion.place_data.name || '',
      address: suggestion.place_data.address || '',
      phone: suggestion.place_data.phone || '',
      link: suggestion.place_data.website || '',
      place_id: suggestion.place_data.place_id || '',
      lat: suggestion.place_data.location?.lat || '',
      lon: suggestion.place_data.location?.lon || '',
      // City from suggestion
      city: suggestion.city || 'tel-aviv',
      // Deal information
      main_offer: suggestion.deal_description || '',
      offer_details: suggestion.offer_details || '',
      // Keep initialSearchQuery as fallback for manual search
      initialSearchQuery: suggestion.place_data.name || ''
    };

    // Use encodeURIComponent for proper UTF-8/Unicode support
    // This handles Hebrew and other non-Latin characters correctly
    const encodeForUrl = (obj) => {
      const str = JSON.stringify(obj || {});
      return encodeURIComponent(str);
    };

    const stateData = encodeForUrl(payload);

    logger.info('Opening add venue page with pre-filled data:', {
      name: suggestion.place_data.name,
      place_id: suggestion.place_data.place_id,
      city: suggestion.city,
      description: suggestion.deal_description
    });

    window.open(`/admin/venues/new?state=${stateData}`, '_blank');
  };

  const filteredSuggestions = suggestions.filter(suggestion => {
    const matchesStatus = filter === 'all' || suggestion.status === filter;
    const matchesCity = cityFilter === 'all' || suggestion.city === cityFilter;
    return matchesStatus && matchesCity;
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All Suggestions</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
            >
              <MenuItem value="all">All Cities</MenuItem>
              {getAllCities().map((c) => (
                <MenuItem key={c.slug} value={c.slug}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredSuggestions.length} suggestions
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : filteredSuggestions.length === 0 ? (
        <Alert severity="info">No deal suggestions found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Deal Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuggestions.map((suggestion) => (
                <TableRow key={suggestion.id}>
                  <TableCell>
                    {format(suggestion.created_at, 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCityBySlug(suggestion.city)?.shortName || 'TLV'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {suggestion.place_data.name}
                      </Typography>
                      {suggestion.place_data.place_id && (
                        <Chip
                          label="Google"
                          size="small"
                          color="success"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {suggestion.place_data.address}
                    </Typography>
                    {suggestion.metadata?.source && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                        Source: {suggestion.metadata.source === 'grid_search_gemini' ? 'Auto-suggest' : suggestion.metadata.source}
                        {suggestion.metadata?.gemini_confidence && ` (${Math.round(suggestion.metadata.gemini_confidence * 100)}% confidence)`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                    {suggestion.deal_description || 'No description provided'}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={suggestion.status || 'pending'}
                      onChange={(e) => handleStatusChange(suggestion.id, e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleCreateVenue(suggestion)}
                        disabled={suggestion.status === 'complete'}
                      >
                        Create Venue
                      </Button>
                      <Tooltip title="Delete suggestion">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(suggestion.id, suggestion.place_data.name)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DealSuggestionsTable;